import {
  ArrowDownOutlined,
  BulbOutlined,
  FilterOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { Pagination, Skeleton } from 'antd'
import React, { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router'
import styled from 'styled-components'
import { Button as SutraButton } from 'sutra/components/Button'
import { Callout } from 'sutra/components/Callout/Callout'
import { Chiclet } from 'sutra/components/Chiclet/Chiclet'
import { Tabs } from 'sutra/components/Tabs/Tabs'

import { darkBlueFontColor, pageBackgroundGrey } from 'assets/styles/variables'
import { TrendLineIcon } from 'features/trend-detection/components/IssueAgentMetaRow'
import { IssueCard } from 'features/trend-detection/components/IssueCard'
import { DetectedIssueIcon } from 'features/trend-detection/components/IssueShared'
import { IssueUpdateCard } from 'features/trend-detection/components/IssueUpdateCard'
import { JumpCard } from 'features/trend-detection/components/JumpCard'
import { GapMeasurementIcon } from 'features/trend-detection/features/benchmarking-agent/GapStrengthBadge'
import {
  BenchmarkingFeedContainer,
  type BenchmarkingFeedStats,
} from 'features/trend-detection/features/benchmarking-feed/Container'
import { AgentInsightFeedContainer } from 'features/trend-detection/features/insight-agent/Container'
import type { AgentInsightFeedStats } from 'features/trend-detection/features/insight-agent/Container'
import { KeyInsightsSection } from 'features/trend-detection/features/issue-feed/components/KeyInsightsSection'
import {
  AgentFilterSelect,
  CardStack,
  DiscoverMoreSection,
  DiscoverMoreStack,
  EmptyHint,
  FeedContentPanel,
  FeedSectionHeaderBlock,
  FeedSectionHeading,
  FeedSectionSubheading,
  FeedSkeletonCard,
  FeedSkeletonStack,
  FeedTabsWrapper,
  FeedToolbarLeft,
  FeedToolbarRight,
  FeedToolbarRow,
  IssuesContainer,
  PaginationPageSummary,
  PaginationWrapper,
  SortSelect,
  SubsectionCard,
  SubsectionCardIcon,
  SubsectionCardSubtitle,
  SubsectionCardTextBlock,
  SubsectionCardTitle,
  SubsectionSelectorRow,
  ToolbarSelectPrefixIcon,
  TrackingCountBadge,
  ViewPreviousFeedButton,
  ViewPreviousFeedSubtext,
  ViewPreviousFeedTitleRow,
} from 'features/trend-detection/features/issue-feed/feed-styles'
import type {
  IssuesFeedSort,
  UpdatesFeedSort,
} from 'features/trend-detection/features/issue-feed/sort-types'
import { isFeedSuppressedWhileAgentRunning } from 'features/trend-detection/features/issue-feed/utils/feed-empty-state'
import {
  ISSUE_DETECTION_AGENT_TYPE,
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import { trendDetectionAgentsPath } from 'features/trend-detection/paths'
import type {
  AgentFeedOption,
  BenchmarkingInsightFeedItem,
  Chart,
  IssueCardData,
  IssueFeedPeriod,
  KeyInsightItem,
  TrendFeedBannerAgent,
  TrendFeedIssueItem,
  TrendFeedUpdateItem,
} from 'features/trend-detection/types'
import { getEmergenceLevel } from 'features/trend-detection/utils/helpers'

const FEED_SKELETON_CARD_COUNT = 4
const ISSUE_FEED_PAGE_SIZE = 15

function pagedItemSummary(
  onPage: number,
  total: number | null,
  singular: string,
  plural: string,
): string {
  const t = total ?? 0
  const noun = t === 1 ? singular : plural
  return `(${onPage} of ${t} ${noun})`
}

function FeedTabSkeleton(): React.ReactElement {
  return (
    <FeedSkeletonStack>
      {Array.from({ length: FEED_SKELETON_CARD_COUNT }).map((_, index) => (
        <FeedSkeletonCard key={index}>
          <Skeleton active paragraph={{ rows: 3 }} />
        </FeedSkeletonCard>
      ))}
    </FeedSkeletonStack>
  )
}

export interface IssueFeedViewProps {
  projectId: string
  bannerAgents: TrendFeedBannerAgent[]
  agentsForFilter: AgentFeedOption[]
  filterAgentUuid: string | null
  onFilterAgentChange: (uuid: string | null) => void
  issuesItems: TrendFeedIssueItem[]
  updatesItems: TrendFeedUpdateItem[]
  issuesTotalCount: number | null
  updatesTotalCount: number | null
  issuesPage: number
  updatesPage: number
  issuesTotalPages: number
  updatesTotalPages: number
  onIssuesPageChange: (page: number) => void
  onUpdatesPageChange: (page: number) => void
  onOpenSettings: () => void
  onManageAgents: () => void
  issuesFeedLoading: boolean
  updatesFeedLoading: boolean
  issuesSort: IssuesFeedSort
  updatesSort: UpdatesFeedSort
  onIssuesSortChange: (sort: IssuesFeedSort) => void
  onUpdatesSortChange: (sort: UpdatesFeedSort) => void
  isShared: boolean
  feedIssueCards: IssueCardData[]
  chartDataMap: Record<string, Chart>
  chartLoadingMap: Record<string, boolean>
  projectTotalChartMap: Record<string, Chart>
  onIssueTrackingChange?: (issueUuid: string, nextTracked: boolean) => void
  /** Null when not loading any track/untrack request. */
  trackingIssueUuid?: string | null
  updateTimeseriesCharts: Record<string, Chart | null>
  updateTimeseriesLoadingMap: Record<string, boolean>
  updateProjectTotalChartMap: Record<string, Chart>
  /** Source-issue chart fallback for tracked items whose scheduled run hasn't
   * produced an update yet. Keyed by source-issue uuid / run uuid. */
  trackedSourceChartMap: Record<string, Chart>
  trackedSourceChartLoadingMap: Record<string, boolean>
  trackedSourceProjectTotalChartMap: Record<string, Chart>
  /**
   * Top-level page section: Insights vs Tracked. When both are provided, the
   * tab is controlled by the parent (Container.tsx + Mixpanel). When omitted,
   * IssueFeedView falls back to internal state — used by tests and Storybook.
   */
  feedSection?: 'insights' | 'tracked'
  onFeedSectionChange?: (section: 'insights' | 'tracked') => void
  /** Sub-section within Insights: Key Insights, Emerging Issues, or Performance Gaps. */
  feedSubsection?: 'key-insights' | 'issue' | 'benchmarking' | 'agent-insights'
  onFeedSubsectionChange?: (
    subsection: 'key-insights' | 'issue' | 'benchmarking' | 'agent-insights',
  ) => void
  /** Analytics: agent name link on issue card opens agent config. */
  onOpenAgentFromIssueCard?: (ctx: {
    agentUuid: string
    agentName: string
  }) => void
  /** Timestamp of most recent data update from React Query. */
  dataUpdatedAt: number
  /** Triggers refetch of both issues and updates feeds. */
  onRefresh: () => Promise<void>
  /** True while refetch is in flight. */
  isRefreshing: boolean
  sharplyElevatedCount: number
  // Period filter — current vs all-time. The "View previous issues" banner is
  // a second entry point that flips period to all-time.
  period: IssueFeedPeriod
  onPeriodChange: (period: IssueFeedPeriod) => void
  onViewPreviousIssues: () => void
  hasHistoricalIssues: boolean
  // Emergence level filter
  emergenceLevelFilter: string | null
  onEmergenceLevelFilterChange: (level: string | null) => void
  hasBenchmarkingFeed?: boolean
  hasAgentInsightsFeed?: boolean
  /** Filtered cross-agent high-signal items for the Key Insights sub-tab. */
  keyInsights?: KeyInsightItem[]
  keyInsightsLoading?: boolean
  /** Click handler for benchmarking cards rendered inside Key Insights. */
  onBenchmarkingInsightClick?: (insight: BenchmarkingInsightFeedItem) => void
  onDismissBanner: (dismissKey: string) => void
  /** Deep-link seeds forwarded to the benchmarking sub-container (its
   * filter/period state lives inside its own hook, not here). */
  benchmarkingInitialAgentUuidFilter?: string | null
  benchmarkingInitialPeriod?: 'current' | 'all_time'
}

export const IssueFeedView: React.FC<IssueFeedViewProps> = ({
  projectId,
  bannerAgents,
  agentsForFilter,
  filterAgentUuid,
  onFilterAgentChange,
  issuesItems,
  updatesItems,
  issuesTotalCount,
  updatesTotalCount,
  issuesPage,
  updatesPage,
  issuesTotalPages,
  updatesTotalPages,
  onIssuesPageChange,
  onUpdatesPageChange,
  onOpenSettings,
  onManageAgents,
  issuesFeedLoading,
  updatesFeedLoading,
  issuesSort,
  updatesSort,
  onIssuesSortChange,
  onUpdatesSortChange,
  isShared,
  feedIssueCards,
  chartDataMap,
  chartLoadingMap,
  projectTotalChartMap,
  onIssueTrackingChange,
  trackingIssueUuid = null,
  updateTimeseriesCharts,
  updateTimeseriesLoadingMap,
  updateProjectTotalChartMap,
  trackedSourceChartMap,
  trackedSourceChartLoadingMap,
  trackedSourceProjectTotalChartMap,
  feedSection: controlledFeedSection,
  onFeedSectionChange,
  feedSubsection: controlledFeedSubsection,
  onFeedSubsectionChange,
  onOpenAgentFromIssueCard,
  period,
  onPeriodChange,
  onViewPreviousIssues,
  sharplyElevatedCount,
  hasHistoricalIssues,
  hasBenchmarkingFeed = false,
  hasAgentInsightsFeed = false,
  keyInsights = [],
  keyInsightsLoading = false,
  onBenchmarkingInsightClick,
  emergenceLevelFilter,
  onEmergenceLevelFilterChange,
  onDismissBanner,
  benchmarkingInitialAgentUuidFilter,
  benchmarkingInitialPeriod,
}) => {
  const [internalFeedSection, setInternalFeedSection] = useState<
    'insights' | 'tracked'
  >('insights')
  const feedSection = controlledFeedSection ?? internalFeedSection
  const setFeedSection = useCallback(
    (next: 'insights' | 'tracked'): void => {
      if (onFeedSectionChange) onFeedSectionChange(next)
      else setInternalFeedSection(next)
    },
    [onFeedSectionChange],
  )
  const [internalFeedSubsection, setInternalFeedSubsection] = useState<
    'key-insights' | 'issue' | 'benchmarking' | 'agent-insights'
  >('issue')
  const feedSubsection = controlledFeedSubsection ?? internalFeedSubsection
  const setFeedSubsection = useCallback(
    (
      next: 'key-insights' | 'issue' | 'benchmarking' | 'agent-insights',
    ): void => {
      if (onFeedSubsectionChange) onFeedSubsectionChange(next)
      else setInternalFeedSubsection(next)
    },
    [onFeedSubsectionChange],
  )

  // The IssueCard `feedTab` analytics tag still describes which list the card
  // came from (detected vs tracked); it's not the page-level section state.
  const cardFeedTab: 'feed' | 'tracked' =
    feedSection === 'tracked' ? 'tracked' : 'feed'

  const updatesBadgeCount = updatesTotalCount === null ? '…' : updatesTotalCount

  const issueCardByUuid = useMemo(
    () => new Map(feedIssueCards.map((c) => [c.issue_uuid ?? '', c])),
    [feedIssueCards],
  )

  const [benchmarkingStats, setBenchmarkingStats] =
    useState<BenchmarkingFeedStats | null>(null)
  const handleBenchmarkingStats = useCallback(
    (stats: BenchmarkingFeedStats) => setBenchmarkingStats(stats),
    [],
  )

  const [agentInsightStats, setAgentInsightStats] =
    useState<AgentInsightFeedStats | null>(null)
  const handleAgentInsightStats = useCallback(
    (stats: AgentInsightFeedStats) => setAgentInsightStats(stats),
    [],
  )

  // Jump-card counts. Each is "additional items past what's already shown in
  // Key Insights" so the cards quantify what the user gains by jumping.
  // Counts clamp to 0 in case Key Insights ever surfaces more items than the
  // full feed reports during a race (e.g. mid-refetch).
  const trackedIssuesCount = updatesTotalCount ?? 0
  const keyInsightsIssueCount = useMemo(
    () => keyInsights.filter((k) => k.type === 'issue').length,
    [keyInsights],
  )
  const keyInsightsGapCount = useMemo(
    () => keyInsights.filter((k) => k.type === 'benchmarking').length,
    [keyInsights],
  )
  const moreIssuesCount =
    issuesTotalCount === null
      ? null
      : Math.max(0, issuesTotalCount - keyInsightsIssueCount)
  const moreGapsCount =
    !hasBenchmarkingFeed ||
    benchmarkingStats == null ||
    benchmarkingStats.isLoading
      ? null
      : Math.max(0, benchmarkingStats.totalCount - keyInsightsGapCount)

  const showTrackedJumpCard = trackedIssuesCount > 0
  const showMoreIssuesJumpCard = moreIssuesCount !== null && moreIssuesCount > 0
  const showMoreGapsJumpCard = moreGapsCount !== null && moreGapsCount > 0
  const showDiscoverMore = showMoreIssuesJumpCard || showMoreGapsJumpCard

  const handleTrackedJumpCardClick = useCallback(() => {
    trackResearchAgents(ResearchAgentsAction.jump_card_to_tracked, {
      count: trackedIssuesCount,
    })
    setFeedSection('tracked')
  }, [trackedIssuesCount, setFeedSection])
  const handleMoreIssuesJumpCardClick = useCallback(() => {
    trackResearchAgents(ResearchAgentsAction.jump_card_to_issues, {
      count: moreIssuesCount ?? 0,
    })
    setFeedSubsection('issue')
  }, [moreIssuesCount, setFeedSubsection])
  const handleMoreGapsJumpCardClick = useCallback(() => {
    trackResearchAgents(ResearchAgentsAction.jump_card_to_benchmarking, {
      count: moreGapsCount ?? 0,
    })
    setFeedSubsection('benchmarking')
  }, [moreGapsCount, setFeedSubsection])

  const showDetectedRunningEmpty =
    !issuesFeedLoading &&
    issuesItems.length === 0 &&
    isFeedSuppressedWhileAgentRunning(
      filterAgentUuid,
      bannerAgents,
      agentsForFilter,
    )
  const showTrackedRunningEmpty =
    !updatesFeedLoading &&
    updatesItems.length === 0 &&
    isFeedSuppressedWhileAgentRunning(
      filterAgentUuid,
      bannerAgents,
      agentsForFilter,
    )

  const agentFilterToolbarSelect = (
    <AgentFilterSelect
      allowClear
      aria-label="Filter by agent"
      placeholder="All Agents"
      triggerIcon={
        <ToolbarSelectPrefixIcon aria-hidden>
          <FilterOutlined />
        </ToolbarSelectPrefixIcon>
      }
      value={filterAgentUuid ?? undefined}
      onChange={(v) => onFilterAgentChange(typeof v === 'string' ? v : null)}
      options={agentsForFilter.map((a) => ({
        value: a.uuid,
        label: a.name,
      }))}
      optionRender={(option) =>
        agentsForFilter.find((a) => a.uuid === option.value)?.is_default ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              minWidth: 0,
              overflow: 'hidden',
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {option.label}
            </span>
            <Chiclet $tone="blue" $size="sm">
              Default
            </Chiclet>
          </span>
        ) : (
          option.label
        )
      }
    />
  )

  return (
    <PageContainer>
      <ContentWrapper>
        <PageHeaderBlock>
          <TopBar>
            <TitleGroup>
              <PageTitle>Insights</PageTitle>
            </TitleGroup>
            <TopBarActions>
              <SutraButton
                $variant="secondary"
                $size="sm"
                $height="40px"
                onClick={onManageAgents}
              >
                Manage agents
              </SutraButton>
              <SutraButton
                $variant="primary"
                $size="sm"
                $height="40px"
                onClick={onOpenSettings}
              >
                Create agent
              </SutraButton>
            </TopBarActions>
          </TopBar>
          <SubtitleRow>
            <PageSubtitle>
              Real-time intelligence from your Yogi agents
            </PageSubtitle>
          </SubtitleRow>
        </PageHeaderBlock>

        {bannerAgents.length > 0 && (
          <BannerStack>
            {bannerAgents.map((b) => {
              return (
                <Callout
                  role="alert"
                  key={`${b.agent_uuid}-${b.status}`}
                  variant={b.status === 'failed' ? 'danger' : 'info'}
                  onClose={() => onDismissBanner(b.run_uuid ?? b.agent_uuid)}
                  title={
                    b.status === 'running'
                      ? `${b.agent_name} has a run in progress — results shown may be incomplete.`
                      : `${b.agent_name}'s last run failed. Results are from the previous completed run.`
                  }
                  action={
                    b.status === 'failed' ? (
                      <Link to={trendDetectionAgentsPath(projectId)}>
                        Manage agents
                      </Link>
                    ) : undefined
                  }
                />
              )
            })}
          </BannerStack>
        )}

        <FeedResultsSection>
          <FeedTabsWrapper>
            <Tabs
              activeKey={feedSection}
              onChange={(key) => setFeedSection(key as 'insights' | 'tracked')}
              items={[
                {
                  key: 'insights',
                  label: (
                    <TabLabel data-chameleon="issue-feed-feed-tab">
                      Feed
                    </TabLabel>
                  ),
                  children: (
                    <>
                      <SubsectionSelectorRow>
                        <SubsectionCard
                          $active={feedSubsection === 'key-insights'}
                          onClick={() => setFeedSubsection('key-insights')}
                          data-chameleon="issue-feed-key-insights-tab"
                          type="button"
                        >
                          <SubsectionCardIcon aria-hidden>
                            <BulbOutlined
                              style={{ fontSize: 20, color: '#28364F94' }}
                            />
                          </SubsectionCardIcon>
                          <SubsectionCardTextBlock>
                            <SubsectionCardTitle>
                              Key insights
                            </SubsectionCardTitle>
                            <SubsectionCardSubtitle>
                              Top issues & gaps
                            </SubsectionCardSubtitle>
                          </SubsectionCardTextBlock>
                        </SubsectionCard>
                        <SubsectionCard
                          $active={feedSubsection === 'issue'}
                          onClick={() => setFeedSubsection('issue')}
                          data-chameleon="issue-feed-issue-insights-tab"
                          type="button"
                        >
                          <SubsectionCardIcon aria-hidden>
                            <DetectedIssueIcon
                              color="#28364F94"
                              width={24}
                              height={24}
                            />
                          </SubsectionCardIcon>
                          <SubsectionCardTextBlock>
                            <SubsectionCardTitle>
                              {issuesTotalCount !== null
                                ? `${issuesTotalCount} Emerging ${issuesTotalCount === 1 ? 'issue' : 'issues'}`
                                : 'Emerging issues'}
                            </SubsectionCardTitle>
                            <SubsectionCardSubtitle>
                              {sharplyElevatedCount > 0
                                ? `${sharplyElevatedCount} sharply elevated`
                                : issuesFeedLoading
                                  ? ' '
                                  : 'None sharply elevated'}
                            </SubsectionCardSubtitle>
                          </SubsectionCardTextBlock>
                        </SubsectionCard>
                        <SubsectionCard
                          $active={feedSubsection === 'benchmarking'}
                          onClick={() => setFeedSubsection('benchmarking')}
                          data-chameleon="issue-feed-benchmarking-tab"
                          type="button"
                        >
                          <SubsectionCardIcon aria-hidden>
                            <GapMeasurementIcon
                              direction="leading"
                              color="#28364F94"
                              width={24}
                              height={24}
                            />
                          </SubsectionCardIcon>
                          <SubsectionCardTextBlock>
                            <SubsectionCardTitle>
                              {hasBenchmarkingFeed &&
                              benchmarkingStats != null &&
                              !benchmarkingStats.isLoading
                                ? `${benchmarkingStats.totalCount} Performance ${benchmarkingStats.totalCount === 1 ? 'gap' : 'gaps'}`
                                : 'Performance gaps'}
                            </SubsectionCardTitle>
                            <SubsectionCardSubtitle>
                              {!hasBenchmarkingFeed
                                ? 'None yet'
                                : benchmarkingStats == null ||
                                    benchmarkingStats.isLoading
                                  ? ' '
                                  : benchmarkingStats.laggingCount > 0 ||
                                      benchmarkingStats.leadingCount > 0
                                    ? `${benchmarkingStats.laggingCount} lagging · ${benchmarkingStats.leadingCount} leading`
                                    : 'None significant'}
                            </SubsectionCardSubtitle>
                          </SubsectionCardTextBlock>
                        </SubsectionCard>
                        {hasAgentInsightsFeed && (
                          <SubsectionCard
                            $active={feedSubsection === 'agent-insights'}
                            onClick={() => setFeedSubsection('agent-insights')}
                            data-chameleon="issue-feed-agent-insights-tab"
                            type="button"
                          >
                            <SubsectionCardIcon aria-hidden>
                              <TrendLineIcon width={20} height={18} />
                            </SubsectionCardIcon>
                            <SubsectionCardTextBlock>
                              <SubsectionCardTitle>
                                {agentInsightStats != null &&
                                !agentInsightStats.isLoading
                                  ? `${agentInsightStats.totalCount} Agent ${agentInsightStats.totalCount === 1 ? 'insight' : 'insights'}`
                                  : 'Agent insights'}
                              </SubsectionCardTitle>
                              <SubsectionCardSubtitle>
                                {agentInsightStats == null ||
                                agentInsightStats.isLoading
                                  ? ' '
                                  : agentInsightStats.totalCount > 0
                                    ? 'From your insight agents'
                                    : 'None yet'}
                              </SubsectionCardSubtitle>
                            </SubsectionCardTextBlock>
                          </SubsectionCard>
                        )}
                      </SubsectionSelectorRow>
                      {feedSubsection === 'issue' && (
                        <FeedContentPanel $flush>
                          <FeedToolbarRow>
                            <FeedToolbarLeft>
                              <SortSelect
                                aria-label="Period"
                                data-chameleon="issue-feed-period"
                                triggerIcon={
                                  <ToolbarSelectPrefixIcon aria-hidden>
                                    <FilterOutlined />
                                  </ToolbarSelectPrefixIcon>
                                }
                                value={period}
                                onChange={(v) =>
                                  onPeriodChange(v as IssueFeedPeriod)
                                }
                                options={[
                                  { value: 'current', label: 'Current Period' },
                                  { value: 'all_time', label: 'Full History' },
                                ]}
                              />
                              <SortSelect
                                aria-label="Filter by emergence level"
                                allowClear
                                placeholder="All Elevation Levels"
                                triggerIcon={
                                  <ToolbarSelectPrefixIcon aria-hidden>
                                    <FilterOutlined />
                                  </ToolbarSelectPrefixIcon>
                                }
                                value={emergenceLevelFilter ?? undefined}
                                onChange={(v) =>
                                  onEmergenceLevelFilterChange(
                                    (v as string) ?? null,
                                  )
                                }
                                options={[
                                  {
                                    value: 'early_signs',
                                    label: 'Slightly Elevated',
                                  },
                                  {
                                    value: 'escalating',
                                    label: 'Elevated',
                                  },
                                  {
                                    value: 'surging',
                                    label: 'Sharply Elevated',
                                  },
                                ]}
                              />
                              {agentFilterToolbarSelect}
                            </FeedToolbarLeft>
                            <FeedToolbarRight>
                              <SortSelect
                                aria-label="Sort issues"
                                data-chameleon="issue-feed-sort"
                                triggerIcon={
                                  <ToolbarSelectPrefixIcon aria-hidden>
                                    <ArrowDownOutlined />
                                  </ToolbarSelectPrefixIcon>
                                }
                                value={issuesSort}
                                onChange={(v) =>
                                  onIssuesSortChange(v as IssuesFeedSort)
                                }
                                options={[
                                  {
                                    value: 'emergence',
                                    label: 'Most Elevated',
                                  },
                                  { value: 'mentions', label: 'Most Records' },
                                ]}
                              />
                            </FeedToolbarRight>
                          </FeedToolbarRow>
                          <FeedSectionHeaderBlock>
                            <FeedSectionHeading>
                              Emerging issues
                            </FeedSectionHeading>
                            <FeedSectionSubheading>
                              Stay on top of concerns appearing in recent
                              feedback.
                            </FeedSectionSubheading>
                          </FeedSectionHeaderBlock>
                          {issuesFeedLoading ? (
                            <FeedTabSkeleton />
                          ) : issuesItems.length === 0 ? (
                            showDetectedRunningEmpty ? (
                              <EmptyHint>
                                Results are temporarily hidden while this agent
                                is running. Check back in a moment for the
                                latest results.
                              </EmptyHint>
                            ) : (
                              <EmptyHint>No results found.</EmptyHint>
                            )
                          ) : (
                            <>
                              <IssuesContainer>
                                {issuesItems.map((item, idx) => {
                                  const issueUuid = item.issue.uuid
                                  const key = `${item.run_uuid}:${issueUuid}`
                                  const issueCard =
                                    issueCardByUuid.get(issueUuid) ??
                                    feedIssueCards[idx]
                                  if (!issueCard) return null
                                  const emergenceLevel = getEmergenceLevel(
                                    issueCard.emergence_score_normalized,
                                  )
                                  return (
                                    <CardStack key={key}>
                                      <IssueCard
                                        issueCard={issueCard}
                                        issue={item.issue}
                                        issueIndex={idx}
                                        emergenceLevel={emergenceLevel}
                                        timeseriesChart={
                                          chartDataMap[issueUuid]
                                        }
                                        chartLoading={
                                          chartLoadingMap[issueUuid] ?? false
                                        }
                                        projectTotalChart={
                                          projectTotalChartMap[item.run_uuid] ??
                                          null
                                        }
                                        agentUuid={item.agent_uuid}
                                        agentName={item.agent_name}
                                        agentType={ISSUE_DETECTION_AGENT_TYPE}
                                        runUuid={item.run_uuid}
                                        feedTab={cardFeedTab}
                                        source="emerging_issues"
                                        onAgentNameNavigate={
                                          onOpenAgentFromIssueCard
                                            ? () =>
                                                onOpenAgentFromIssueCard({
                                                  agentUuid: item.agent_uuid,
                                                  agentName: item.agent_name,
                                                })
                                            : undefined
                                        }
                                        isShared={isShared}
                                        projectId={projectId}
                                        onIssueTrackingChange={
                                          onIssueTrackingChange
                                            ? (next) =>
                                                onIssueTrackingChange(
                                                  issueUuid,
                                                  next,
                                                )
                                            : undefined
                                        }
                                        trackingLoading={
                                          trackingIssueUuid === issueUuid
                                        }
                                      />
                                    </CardStack>
                                  )
                                })}
                              </IssuesContainer>
                              {issuesTotalPages > 1 && (
                                <PaginationWrapper>
                                  <PaginationPageSummary
                                    aria-label={pagedItemSummary(
                                      issuesItems.length,
                                      issuesTotalCount,
                                      'issue',
                                      'issues',
                                    )}
                                  >
                                    {pagedItemSummary(
                                      issuesItems.length,
                                      issuesTotalCount,
                                      'issue',
                                      'issues',
                                    )}
                                  </PaginationPageSummary>
                                  <Pagination
                                    current={issuesPage}
                                    total={issuesTotalCount ?? 0}
                                    pageSize={ISSUE_FEED_PAGE_SIZE}
                                    showSizeChanger={false}
                                    onChange={(page) =>
                                      onIssuesPageChange(page)
                                    }
                                  />
                                </PaginationWrapper>
                              )}
                            </>
                          )}
                          {!issuesFeedLoading &&
                            period === 'current' &&
                            hasHistoricalIssues && (
                              <ViewPreviousFeedButton
                                onClick={onViewPreviousIssues}
                              >
                                <ViewPreviousFeedTitleRow>
                                  <HistoryOutlined />
                                  View earlier issues
                                </ViewPreviousFeedTitleRow>
                                <ViewPreviousFeedSubtext>
                                  Explore issues detected in past runs.
                                </ViewPreviousFeedSubtext>
                              </ViewPreviousFeedButton>
                            )}
                        </FeedContentPanel>
                      )}
                      <FeedContentPanel
                        $flush
                        $hidden={feedSubsection !== 'benchmarking'}
                      >
                        <BenchmarkingFeedContainer
                          projectId={projectId}
                          enabled={hasBenchmarkingFeed}
                          onStats={handleBenchmarkingStats}
                          initialAgentUuidFilter={
                            benchmarkingInitialAgentUuidFilter
                          }
                          initialPeriod={benchmarkingInitialPeriod}
                        />
                      </FeedContentPanel>
                      <FeedContentPanel
                        $flush
                        $hidden={feedSubsection !== 'agent-insights'}
                      >
                        <AgentInsightFeedContainer
                          projectId={projectId}
                          enabled={hasAgentInsightsFeed}
                          onStats={handleAgentInsightStats}
                        />
                      </FeedContentPanel>
                      {feedSubsection === 'key-insights' && (
                        <FeedContentPanel>
                          <FeedSectionHeaderBlock>
                            <FeedSectionHeading>
                              Key insights
                            </FeedSectionHeading>
                            <FeedSectionSubheading>
                              A focused view of top issues and performance gaps.
                            </FeedSectionSubheading>
                          </FeedSectionHeaderBlock>
                          {showTrackedJumpCard && (
                            <JumpCard
                              icon={<TrendLineIcon width={20} height={18} />}
                              title={`${trackedIssuesCount} ${trackedIssuesCount === 1 ? 'issue' : 'issues'} you’re actively monitoring`}
                              subtitle="Check in on recent updates"
                              ctaLabel="View tracked issues"
                              onClick={handleTrackedJumpCardClick}
                              dataChameleon="issue-feed-jump-card-tracked"
                            />
                          )}
                          <KeyInsightsSection
                            items={keyInsights}
                            loading={keyInsightsLoading}
                            projectId={projectId}
                            isShared={isShared}
                            onIssueTrackingChange={onIssueTrackingChange}
                            trackingIssueUuid={trackingIssueUuid}
                            onBenchmarkingInsightClick={
                              onBenchmarkingInsightClick ?? (() => undefined)
                            }
                            onAgentNameNavigate={onOpenAgentFromIssueCard}
                            chartDataMap={chartDataMap}
                            chartLoadingMap={chartLoadingMap}
                            projectTotalChartMap={projectTotalChartMap}
                          />
                          {showDiscoverMore && (
                            <DiscoverMoreSection>
                              <FeedSectionHeaderBlock>
                                <FeedSectionHeading>
                                  Discover more
                                </FeedSectionHeading>
                                <FeedSectionSubheading>
                                  Additional issues and performance gaps
                                  discovered by your agents.
                                </FeedSectionSubheading>
                              </FeedSectionHeaderBlock>
                              <DiscoverMoreStack>
                                {showMoreIssuesJumpCard && (
                                  <JumpCard
                                    icon={
                                      <DetectedIssueIcon
                                        color="#868D98"
                                        width={18}
                                        height={18}
                                      />
                                    }
                                    title={`${moreIssuesCount} ${keyInsightsIssueCount > 0 ? 'more ' : ''}${moreIssuesCount === 1 ? 'issue' : 'issues'} to review`}
                                    subtitle="Review all issues your agents found."
                                    ctaLabel="View all emerging issues"
                                    onClick={handleMoreIssuesJumpCardClick}
                                    variant="outlined"
                                    dataChameleon="issue-feed-jump-card-issues"
                                  />
                                )}
                                {showMoreGapsJumpCard && (
                                  <JumpCard
                                    icon={
                                      <GapMeasurementIcon
                                        direction="leading"
                                        color="#868D98"
                                        width={18}
                                        height={18}
                                      />
                                    }
                                    title={`${moreGapsCount} ${keyInsightsGapCount > 0 ? 'more ' : ''}performance ${moreGapsCount === 1 ? 'gap' : 'gaps'} to explore`}
                                    subtitle="Explore all gaps your agents identified."
                                    ctaLabel="View all performance gaps"
                                    onClick={handleMoreGapsJumpCardClick}
                                    variant="outlined"
                                    dataChameleon="issue-feed-jump-card-benchmarking"
                                  />
                                )}
                              </DiscoverMoreStack>
                            </DiscoverMoreSection>
                          )}
                        </FeedContentPanel>
                      )}
                    </>
                  ),
                },
                {
                  // Updates from the feed API; track/untrack only updates per-card state, not list membership or counts (those change on new runs).
                  key: 'tracked',
                  label: (
                    <TabLabel data-chameleon="issue-feed-tracked-tab">
                      Tracked
                      {updatesTotalCount !== 0 && (
                        <TrackingCountBadge>
                          {updatesBadgeCount}
                        </TrackingCountBadge>
                      )}
                    </TabLabel>
                  ),
                  children: (
                    <FeedContentPanel $flush>
                      <FeedToolbarRow>
                        <FeedToolbarLeft>
                          {agentFilterToolbarSelect}
                        </FeedToolbarLeft>
                        <FeedToolbarRight>
                          <SortSelect
                            aria-label="Sort updates"
                            triggerIcon={
                              <ToolbarSelectPrefixIcon aria-hidden>
                                <ArrowDownOutlined />
                              </ToolbarSelectPrefixIcon>
                            }
                            value={updatesSort}
                            onChange={(v) =>
                              onUpdatesSortChange(v as UpdatesFeedSort)
                            }
                            options={[
                              {
                                value: 'most_changing',
                                label: 'Most Changing',
                              },
                              {
                                value: 'most_escalating',
                                label: 'Most Elevated',
                              },
                              {
                                value: 'records_first',
                                label: 'Most Records',
                              },
                            ]}
                          />
                        </FeedToolbarRight>
                      </FeedToolbarRow>
                      {updatesFeedLoading ? (
                        <FeedTabSkeleton />
                      ) : updatesItems.length === 0 ? (
                        showTrackedRunningEmpty ? (
                          <EmptyHint>
                            Updates are temporarily hidden while this agent is
                            running. Check back in a moment for the latest
                            changes.
                          </EmptyHint>
                        ) : (
                          <EmptyHint>Track issues to see them here.</EmptyHint>
                        )
                      ) : (
                        <>
                          <IssuesContainer>
                            {updatesItems.map((item) => {
                              // `item.update` is null for newly-tracked issues
                              // that haven't been swept by a scheduled run yet.
                              // The card renders the source issue without an
                              // "Updated …" badge or impact-rate footer in
                              // that state.
                              const u = item.update
                              const sourceIssue = item.source_issue
                              const key = u
                                ? `${item.run_uuid}:${u.uuid}`
                                : `tracked:${sourceIssue.uuid}`
                              return (
                                <CardStack key={key}>
                                  <IssueUpdateCard
                                    update={u}
                                    sourceIssue={sourceIssue}
                                    agentUuid={item.agent_uuid}
                                    agentName={item.agent_name}
                                    timeseriesChart={
                                      u
                                        ? (updateTimeseriesCharts[u.uuid] ??
                                          null)
                                        : (trackedSourceChartMap[
                                            sourceIssue.uuid
                                          ] ?? null)
                                    }
                                    timeseriesLoading={
                                      u
                                        ? (updateTimeseriesLoadingMap[u.uuid] ??
                                          false)
                                        : (trackedSourceChartLoadingMap[
                                            sourceIssue.uuid
                                          ] ?? false)
                                    }
                                    projectTotalChart={
                                      u
                                        ? (updateProjectTotalChartMap[u.uuid] ??
                                          null)
                                        : (trackedSourceProjectTotalChartMap[
                                            item.run_uuid
                                          ] ?? null)
                                    }
                                    isShared={isShared}
                                    projectId={projectId}
                                    onIssueTrackingChange={
                                      onIssueTrackingChange
                                        ? (next) =>
                                            onIssueTrackingChange(
                                              sourceIssue.uuid,
                                              next,
                                            )
                                        : undefined
                                    }
                                    trackingLoading={
                                      trackingIssueUuid === sourceIssue.uuid
                                    }
                                  />
                                </CardStack>
                              )
                            })}
                          </IssuesContainer>
                          {updatesTotalPages > 1 && (
                            <PaginationWrapper>
                              <PaginationPageSummary
                                aria-label={pagedItemSummary(
                                  updatesItems.length,
                                  updatesTotalCount,
                                  'update',
                                  'updates',
                                )}
                              >
                                {pagedItemSummary(
                                  updatesItems.length,
                                  updatesTotalCount,
                                  'update',
                                  'updates',
                                )}
                              </PaginationPageSummary>
                              <Pagination
                                current={updatesPage}
                                total={updatesTotalCount ?? 0}
                                pageSize={ISSUE_FEED_PAGE_SIZE}
                                showSizeChanger={false}
                                onChange={(page) => onUpdatesPageChange(page)}
                              />
                            </PaginationWrapper>
                          )}
                        </>
                      )}
                    </FeedContentPanel>
                  ),
                },
              ]}
            />
          </FeedTabsWrapper>
        </FeedResultsSection>
      </ContentWrapper>
    </PageContainer>
  )
}

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 600;
  line-height: 1.2;
  color: ${darkBlueFontColor};
  margin: 0;
`

const PageContainer = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
  background: ${pageBackgroundGrey};
`

const ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
`

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const PageSubtitle = styled.p`
  margin: 0;
  font-family: 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 1;
  letter-spacing: 0;
  color: #28364f;
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`

const TopBarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const PageHeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const BannerStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FeedResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
`

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
`
